/* Push natifs (app fermée / arrière-plan) — @capacitor/push-notifications.
   À appeler APRÈS une connexion restaurant/livreur réussie (jamais au premier
   lancement). En premier plan, la cloche in-app existante reste le seul canal
   (le listener pushNotificationReceived n'affiche volontairement rien). */
import { Capacitor } from "@capacitor/core";
import { apiClient } from "@/lib/api";

let registered = false;

/** Enregistre l'appareil pour les push natifs. Retourne :
    "granted" | "denied" | "unsupported" */
export async function registerNativePush() {
  if (!Capacitor.isNativePlatform()) return "unsupported";
  const { PushNotifications } = await import("@capacitor/push-notifications");

  // Canal Android IMPORTANCE MAX + son personnalisé (res/raw/alert.wav),
  // sonne même écran verrouillé. À créer AVANT tout envoi.
  if (Capacitor.getPlatform() === "android") {
    try {
      await PushNotifications.createChannel({
        id: "max_alerts",
        name: "Commandes & courses",
        description: "Nouvelle commande / nouvelle course — alerte sonore prioritaire",
        importance: 5,
        sound: "alert",
        vibration: true,
        lights: true,
      });
      await PushNotifications.createChannel({
        id: "neldaari_default",
        name: "Autres notifications",
        description: "Paiements, avis clients et informations",
        importance: 3,
        vibration: true,
      });
    } catch (e) { /* canaux déjà créés */ }
  }

  let perm = await PushNotifications.checkPermissions();
  if (perm.receive === "prompt" || perm.receive === "prompt-with-rationale") {
    perm = await PushNotifications.requestPermissions();
  }
  if (perm.receive !== "granted") return "denied";

  if (!registered) {
    registered = true;
    await PushNotifications.addListener("registration", async (token) => {
      try {
        await apiClient.post("/devices", { token: token.value, platform: Capacitor.getPlatform() });
      } catch (e) { /* réessayé au prochain lancement */ }
    });
    await PushNotifications.addListener("registrationError", () => {});
    // Premier plan : pas de bannière système — l'écran d'alerte web (IncomingAlertOverlay)
    // prend le relais dès qu'un « ring » arrive.
    await PushNotifications.addListener("pushNotificationReceived", (n) => {
      if (n?.data?.type === "ring") window.dispatchEvent(new CustomEvent("neldaari:alert", { detail: n.data }));
    });
    await PushNotifications.addListener("pushNotificationActionPerformed", (event) => {
      const data = event?.notification?.data || {};
      const action = event?.actionId || "tap";
      if (data.type === "ring" && data.alert_id) {
        window.dispatchEvent(new CustomEvent("neldaari:alert-action", { detail: { ...data, action } }));
        return;
      }
      const url = data.url;
      if (url && url.startsWith("/")) window.location.href = url;
    });
  }
  await PushNotifications.register();
  const { configureNativeAlerts } = await import("@/utils/incomingAlerts");
  configureNativeAlerts();
  return "granted";
}

/** Ouvre directement les réglages notifications de NelDaari dans le téléphone. */
export async function openNotificationSettings() {
  if (!Capacitor.isNativePlatform()) return false;
  const { NativeSettings, AndroidSettings, IOSSettings } = await import("capacitor-native-settings");
  await NativeSettings.open({
    optionAndroid: AndroidSettings.AppNotification,
    optionIOS: IOSSettings.AppNotification,
  });
  return true;
}

/** true si le téléphone est en silencieux / vibreur ; null si indéterminable. */
export async function isPhoneMuted() {
  if (!Capacitor.isNativePlatform()) return null;
  try {
    const { Mute } = await import("@capgo/capacitor-mute");
    const { value } = await Mute.isMuted();
    return !!value;
  } catch (e) {
    return null;
  }
}

/** Vérifie l'état de permission sans rien demander (pour le rappel dans l'app). */
export async function nativePushPermission() {
  if (!Capacitor.isNativePlatform()) return "unsupported";
  const { PushNotifications } = await import("@capacitor/push-notifications");
  const p = await PushNotifications.checkPermissions();
  return p.receive;
}
